// Contract v1. Keep the customer frontend's analytics/contract.ts identical.
// Legacy emitters retain this version; semantic emitters explicitly use v2.
export * from './contract-v2';
export const ANALYTICS_VERSION = 1;
export const REPLAY_FEATURE = 'analytics_session_replay';
export const HEADERS = {
  device: 'X-Amplitude-Device-Id',
  session: 'X-Amplitude-Session-Id',
  operation: 'X-Analytics-Operation-Id',
  search: 'X-Analytics-Search-Id',
  phase: 'X-Analytics-Search-Phase',
} as const;

export const FRONTEND_EVENTS = [
  'page_viewed',
  'page_engagement',
  'search_submitted',
  'filter_changed',
  'suggestion_selected',
  'ui_interaction',
  'detail_opened',
  'download_created',
  'network_failed',
  'agent_prompt_submitted',
  'application_error',
] as const;
export const BACKEND_EVENTS = [
  'signed_in',
  'registered',
  'signed_out',
  'search_completed',
  'search_page_returned',
  'search_count_resolved',
  'operation_completed',
  'operation_failed',
  'credits_consumed',
  'unlock_completed',
  'unlock_failed',
  'agent_completed',
  'agent_failed',
  'agent_cancelled',
] as const;
export type FrontendEvent = (typeof FRONTEND_EVENTS)[number];
export type BackendEvent = (typeof BACKEND_EVENTS)[number];
export type SearchPhase = 'submit' | 'page' | 'count';
export interface AnalyticsOperation {
  name: string;
  kind: 'search' | 'count' | 'mutation' | 'export' | 'chat';
}

// Explicit customer operations only. No payments, admin APIs, generic HTTP events,
// autocomplete traffic, background ingestion, or unrestricted payload collection.
const OPERATIONS: Array<[string, RegExp, AnalyticsOperation]> = [
  [
    'POST',
    /^\/(data\/fetch_products_by_ingredient|enterprise\/product-search)$/,
    { name: 'products', kind: 'search' },
  ],
  [
    'POST',
    /^\/(data\/fetch_products_by_ingredient|enterprise\/product-search)\/count$/,
    { name: 'products', kind: 'count' },
  ],
  ['POST', /^\/people-v2\/search$/, { name: 'people', kind: 'search' }],
  ['POST', /^\/company\/search$/, { name: 'companies', kind: 'search' }],
  ['GET', /^\/company\/[^/]+$/, { name: 'company_detail', kind: 'mutation' }],
  ['GET', /^\/company\/[^/]+\/people$/, { name: 'company_people', kind: 'search' }],
  ['GET', /^\/company\/[^/]+\/locations$/, { name: 'company_locations', kind: 'mutation' }],
  [
    'GET',
    /^\/company\/[^/]+\/ingredient-group-distribution$/,
    { name: 'company_ingredient_distribution', kind: 'mutation' },
  ],
  ['POST', /^\/data\/fetch_product$/, { name: 'product_detail', kind: 'mutation' }],
  [
    'POST',
    /^\/(data|enterprise)\/accounts\/company-ingredient-group-distribution$/,
    { name: 'account_ingredient_distribution', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/enterprise\/accounts\/company-packaging-distribution$/,
    { name: 'account_packaging_distribution', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/enterprise\/accounts\/company-category-distribution$/,
    { name: 'account_category_distribution', kind: 'mutation' },
  ],
  [
    'GET',
    /^\/enterprise\/company\/[^/]+\/packaging-distribution$/,
    { name: 'company_packaging_distribution', kind: 'mutation' },
  ],
  [
    'GET',
    /^\/enterprise\/company\/[^/]+\/category-distribution$/,
    { name: 'company_category_distribution', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/(enterprise|data)\/accounts$/,
    { name: 'accounts', kind: 'search' },
  ],
  [
    'POST',
    /^\/enterprise\/accounts\/count$/,
    { name: 'accounts', kind: 'count' },
  ],
  [
    'POST',
    /^\/(data\/map_marketers|enterprise\/map-marketers)$/,
    { name: 'map', kind: 'search' },
  ],
  [
    'POST',
    /^\/(enterprise|data)\/accounts\/company-products$/,
    { name: 'company_products', kind: 'search' },
  ],
  [
    'GET',
    /^\/(company\/[^/]+\/products|enterprise\/company\/[^/]+\/category-products)$/,
    { name: 'company_products', kind: 'search' },
  ],
  [
    'POST',
    /^\/people-v2\/fetch_people_from_(products|products_both|product_list|product_list_both)$/,
    { name: 'related_people', kind: 'search' },
  ],
  [
    'POST',
    /^\/(copilot\/chat|data\/chat\/product_search)$/,
    { name: 'agent', kind: 'chat' },
  ],
  ['POST', /^\/user\/logout$/, { name: 'logout', kind: 'mutation' }],
  [
    'POST',
    /^\/user\/create-ticket$/,
    { name: 'support_ticket_created', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/data\/create_product_list$/,
    { name: 'product_list_created', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/data\/add_products_to_list$/,
    { name: 'products_added_to_list', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/data\/product-list\/[^/]+$/,
    { name: 'product_list_deleted', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/data\/product-list\/[^/]+\/items$/,
    { name: 'products_removed_from_list', kind: 'mutation' },
  ],
  [
    'PATCH',
    /^\/data\/product-list\/[^/]+$/,
    { name: 'product_list_renamed', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/people-v2\/list$/,
    { name: 'people_list_created', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/people-v2\/addPeopleToList$/,
    { name: 'people_added_to_list', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/people-v2\/list\/[^/]+$/,
    { name: 'people_list_deleted', kind: 'mutation' },
  ],
  [
    'PATCH',
    /^\/people-v2\/list\/[^/]+$/,
    { name: 'people_list_renamed', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/people-v2\/list\/[^/]+\/[^/]+$/,
    { name: 'people_removed_from_list', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/enterprise\/people\/crm$/,
    { name: 'people_exported_to_crm', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/enterprise\/accounts\/evaluations$/,
    { name: 'account_evaluation_created', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/copilot\/workspace\/action$/,
    { name: 'agent_workspace_changed', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/(copilot\/session\/new|data\/chat\/session\/new)$/,
    { name: 'agent_session_created', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/(copilot|data\/chat)\/session\/[^/]+$/,
    { name: 'agent_session_deleted', kind: 'mutation' },
  ],
  [
    'GET',
    /^\/user\/usage\/export$/,
    { name: 'usage_export_generated', kind: 'export' },
  ],
  [
    'GET',
    /^\/people-v2\/default-list-items-export$/,
    { name: 'people_export_generated', kind: 'export' },
  ],
  [
    'POST|PUT|DELETE',
    /^\/user\/(update-profile|ingredients-of-interest|ingredients-to-replace|ingredient-interest-groups)(\/[^/]+)?$/,
    { name: 'profile_preferences_changed', kind: 'mutation' },
  ],
  [
    'POST',
    /^\/data\/(add_excluded_marketers|add_excluded_manufacturers|add_marketers_of_interest|add_manufacturers_of_interest|add_brands_of_interest)$/,
    { name: 'portfolio_preferences_changed', kind: 'mutation' },
  ],
  [
    'PUT',
    /^\/enterprise\/(category-of-interest\/client-products|claim-of-interest\/claims)$/,
    { name: 'portfolio_preferences_changed', kind: 'mutation' },
  ],
  [
    'DELETE',
    /^\/data\/(remove_excluded_marketers|remove_excluded_manufacturers|remove_marketer_of_interest|clear_marketers_of_interest|remove_manufacturer_of_interest|clear_manufacturers_of_interest|remove_brand_of_interest|clear_brands_of_interest)$/,
    { name: 'portfolio_preferences_changed', kind: 'mutation' },
  ],
  [
    'PUT|PATCH',
    /^\/data\/(portfolio_scope_interests|portfolio_product_interests)$/,
    { name: 'portfolio_preferences_changed', kind: 'mutation' },
  ],
];

export function operationFor(
  method: string,
  path: string,
): AnalyticsOperation | undefined {
  return OPERATIONS.find(
    ([methods, pattern]) =>
      methods.split('|').includes(method.toUpperCase()) &&
      pattern.test(path.replace(/\/$/, '')),
  )?.[2];
}

export function canonicalPage(path: string): string {
  if (['/', '/home', '/dashboard'].includes(path)) return '/home';
  if (['/products', '/search'].includes(path)) return '/products';
  if (/^\/company\/[^/]+\/?$/.test(path)) return '/company/:id';
  return [
    '/people',
    '/accounts',
    '/map',
    '/lists',
    '/profile',
    '/support',
    '/agent',
    '/user/login',
  ].includes(path)
    ? path
    : '/other';
}

// Both sides omit secrets even if a future caller accidentally supplies a form.
// This is a bounded projection, not serialization of a request, response, or entity.
const SECRET_KEY =
  /password|passwd|secret|credential|(?:access|refresh|id)Token|(^|_)(token|access_token|id_token|refresh_token)($|_)|authorization|cookie|captcha|otp|verification.?code|card.?number|cvc|cvv/i;
export function safeProperties(input: unknown): Record<string, any> {
  let remaining = 1000;
  let truncated = false;
  const seen = new WeakSet<object>();
  const visit = (value: unknown, depth: number): any => {
    if (--remaining < 0 || depth > 8) {
      truncated = true;
      return undefined;
    }
    if (value === null || typeof value === 'boolean') return value;
    if (typeof value === 'number')
      return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'string') {
      if (value.length > 2048) truncated = true;
      return value.slice(0, 2048);
    }
    if (!value || typeof value !== 'object' || seen.has(value))
      return undefined;
    seen.add(value);
    if (Array.isArray(value)) {
      if (value.length > 100) truncated = true;
      return value
        .slice(0, 100)
        .map((item) => visit(item, depth + 1))
        .filter((item) => item !== undefined);
    }
    if (
      Object.getPrototypeOf(value) !== Object.prototype &&
      Object.getPrototypeOf(value) !== null
    )
      return undefined;
    const result: Record<string, any> = {};
    const keys = Object.keys(value);
    if (keys.length > 100) truncated = true;
    for (const key of keys.slice(0, 100)) {
      if (
        SECRET_KEY.test(key) ||
        ['__proto__', 'constructor', 'prototype'].includes(key)
      )
        continue;
      const child = visit((value as Record<string, unknown>)[key], depth + 1);
      if (child !== undefined) result[key] = child;
      if (remaining < 0) break;
    }
    return result;
  };
  const result = visit(input, 0) || {};
  if (truncated) result.properties_truncated = true;
  if (JSON.stringify(result).length > 16000) {
    // Retain identifiers and scalar measurements; don't split structured filters.
    const bounded: Record<string, any> = { properties_truncated: true };
    let size = 64;
    for (const [key, value] of Object.entries(result)) {
      if (
        value !== null &&
        !['string', 'number', 'boolean'].includes(typeof value)
      )
        continue;
      size += JSON.stringify([key, value]).length;
      if (size > 15000) break;
      bounded[key] = value;
    }
    return bounded;
  }
  return result;
}

// Compatibility is explicit: validation rejection in a v2 emitter must never
// invoke this wrapper. It exists only for declared unmigrated call sites.
export function legacyEventProperties(input: unknown): Record<string, any> & { schema_version: 1 } {
  return { ...safeProperties(input), schema_version: 1 };
}

const SEARCH_FIELDS = [
  'search',
  'searchTerm',
  'search_term',
  'query',
  'name',
  'searchValue',
  'filters',
  'productFilters',
  'ingredientIds',
  'ingredient_ids',
  'selectedIngredients',
  'module_filters',
  'interestToggles',
  'interestActions',
  'interestSelections',
  'queryMode',
  'compositeGroups',
  'company_type',
  'look_for',
  'product_ids',
  'productIds',
  'organizations',
  'direct_marketer_ids',
  'direct_manufacturer_ids',
  'include_ingredient_interest_ids',
  'exclude_ingredient_interest_ids',
  'composite_query_groups',
  'ingredientInterestGroupIds',
  'coverageIngredientIds',
  'coverageCategoryId',
  'coveragePackagingSelection',
  'categoryId',
  'useCategoryPortfolio',
  'matched_only',
  'rankMatches',
  'combinePositivePackagingFilters',
];
export function searchProperties(
  input: Record<string, any> = {},
): Record<string, any> {
  const selected: Record<string, any> = {};
  for (const key of SEARCH_FIELDS)
    if (input[key] !== undefined) selected[key] = input[key];
  // People search exposes its filter fields directly rather than under `filters`.
  for (const [key, value] of Object.entries(input)) {
    if (
      /^(person_|organization_|organisation_|company_|current_|job_|role_|include_|exclude_|location|industry|skills|headline|keyword|funding|revenue|employee|title)/.test(
        key,
      )
    )
      selected[key] = value;
  }
  // Company product filters are JSON-encoded query parameters on GET requests.
  // Decode only this known field, and keep application request data untouched.
  if (
    typeof selected.productFilters === 'string' &&
    selected.productFilters.length <= 16000
  ) {
    try {
      const parsed = JSON.parse(selected.productFilters);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
        selected.productFilters = parsed;
    } catch {
      /* Keep malformed input bounded; the endpoint owns validation. */
    }
  }
  const criteria = safeProperties(selected);
  const active = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.some(active);
    if (value && typeof value === 'object')
      return Object.values(value).some(active);
    return value != null && value !== false && value !== '';
  };
  const dimensions = new Set<string>();
  const addFields = (values: unknown, prefix = '') => {
    if (!values || typeof values !== 'object' || Array.isArray(values)) return;
    for (const [key, value] of Object.entries(values)) {
      if (active(value)) dimensions.add(`${prefix}${key}`);
    }
  };
  addFields(criteria.filters);
  addFields(criteria.productFilters);
  addFields(criteria.module_filters, 'module_filters.');
  // Include only active portfolio interests, not default include/exclude modes
  // or selections left behind when a toggle is disabled.
  const toggles = criteria.interestToggles || {};
  const selections = criteria.interestSelections || {};
  for (const key of new Set([
    ...Object.keys(toggles),
    ...Object.keys(selections),
  ])) {
    if (
      toggles[key] === true ||
      (toggles[key] === undefined && active(selections[key]))
    )
      dimensions.add(`portfolio.${key}`);
  }
  const metadataFields = new Set([
    'search',
    'searchTerm',
    'search_term',
    'query',
    'name',
    'searchValue',
    'selectedIngredients',
    'queryMode',
    'compositeGroups',
    'composite_query_groups',
    'filters',
    'productFilters',
    'module_filters',
    'interestToggles',
    'interestActions',
    'interestSelections',
    'properties_truncated',
  ]);
  for (const [key, value] of Object.entries(criteria)) {
    if (!metadataFields.has(key) && active(value)) dimensions.add(key);
  }
  const filterKeys = [...dimensions].sort();
  return safeProperties({
    criteria,
    filter_keys: filterKeys,
    filter_count: filterKeys.length,
    filter_combination: filterKeys.join('|'),
  });
}
